import React, { useState, useEffect } from 'react';
import { Search, X, Package, Check, Star, Loader2, Plus, Trash2, Wrench } from 'lucide-react';
import { masterService } from '../../api/masterService';

export default function SelectBestSellerModal({ isOpen, onClose, currentBestSellers = [], onSelect, onRemove }) {
  const [search, setSearch] = useState("");
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadProducts();
    } else {
      setSearch("");
    }
  }, [isOpen]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const [prodRes, svcRes] = await Promise.allSettled([
        masterService.products.listAll ? masterService.products.listAll() : masterService.products.list({ limit: 1000 }),
        masterService.services.listAll ? masterService.services.listAll() : masterService.services.list({ limit: 1000 }),
      ]);
      const items = [];
      if (prodRes.status === "fulfilled") {
        const prodData = Array.isArray(prodRes.value) ? prodRes.value : prodRes.value?.data || [];
        prodData.forEach((p) => {
          items.push({
            id: p.id,
            product_id: p.id,
            name: p.name,
            sku: p.code || p.sku || "",
            category_name: p.category_name || p.category?.name || "Produk",
            image_url: p.image_url,
            item_type: "PRODUCT",
          });
        });
      }
      if (svcRes.status === "fulfilled") {
        const svcData = Array.isArray(svcRes.value) ? svcRes.value : svcRes.value?.data || [];
        svcData.forEach((s) => {
          items.push({
            id: s.id,
            product_id: s.id,
            service_id: s.id,
            name: s.name,
            sku: s.code || "",
            category_name: s.category_name || s.category?.name || "Layanan",
            image_url: s.image_url,
            item_type: "SERVICE",
          });
        });
      }
      setAllProducts(items);
    } catch (error) {
      console.error("Error loading products and services:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const filteredProducts = allProducts
    .filter(p => 
      p.name.toLowerCase().includes(search.toLowerCase()) || 
      (p.sku && p.sku.toLowerCase().includes(search.toLowerCase())) ||
      (p.category_name && p.category_name.toLowerCase().includes(search.toLowerCase()))
    )
    .sort((a, b) => {
      const aSelected = currentBestSellers.includes(a.id);
      const bSelected = currentBestSellers.includes(b.id);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return a.name.localeCompare(b.name);
    });

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 modal-backdrop-enter">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl flex flex-col max-h-[85vh] overflow-hidden modal-panel-enter">
        {/* Header */}
        <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center text-amber-500 ring-1 ring-amber-200/50">
              <Star className="w-5 h-5 fill-current" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800 leading-tight">Kelola Best Seller Manual</h2>
              <p className="text-xs text-slate-500 mt-0.5">Tambah atau hapus produk dan layanan dari daftar best seller</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-slate-100 bg-slate-50/50">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            <input
              type="text"
              placeholder="Cari produk atau layanan berdasarkan nama / kode..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all shadow-sm"
              autoFocus
            />
          </div>
        </div>

        {/* Product & Service List */}
        <div className="flex-1 overflow-y-auto p-3">
          {loading ? (
            <div className="py-12 px-6 flex flex-col items-center justify-center text-center">
              <Loader2 className="w-8 h-8 text-amber-500 animate-spin mb-3" />
              <p className="text-sm font-semibold text-slate-600">Memuat produk & layanan...</p>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="py-12 px-6 flex flex-col items-center justify-center text-center">
              <Package className="w-12 h-12 text-slate-200 mb-3" />
              <p className="text-sm font-semibold text-slate-600">Tidak ada produk atau layanan ditemukan</p>
              <p className="text-xs text-slate-400 mt-1">Coba gunakan kata kunci pencarian yang lain.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredProducts.map(product => {
                const isSelected = currentBestSellers.includes(product.id);
                const isService = product.item_type === "SERVICE";

                return (
                  <div
                    key={product.id}
                    className={`w-full flex items-center justify-between p-3 rounded-xl transition-all ${
                      isSelected 
                        ? "bg-amber-50/60 border border-amber-200/80 shadow-xs" 
                        : "bg-white border border-slate-100 hover:border-amber-200 hover:shadow-xs"
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0 pr-2">
                      <div className="w-10 h-10 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0 relative border border-slate-200/50">
                        {product.image_url ? (
                          <img
                            src={product.image_url.startsWith('http') ? product.image_url : import.meta.env.VITE_API_URL + "/storage/" + product.image_url}
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-slate-300">
                            {isService ? <Wrench className="w-5 h-5 text-blue-400" /> : <Package className="w-5 h-5" />}
                          </div>
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <p className={`text-sm font-semibold truncate ${isSelected ? "text-amber-950" : "text-slate-800"}`}>
                            {product.name}
                          </p>
                          {isSelected && (
                            <span className="flex-shrink-0 text-[10px] font-bold bg-amber-200/80 text-amber-900 px-2 py-0.5 rounded-full">
                              Best Seller
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5 truncate">
                          {isService && (
                            <span className="text-[10px] font-semibold bg-blue-50 text-blue-600 px-1.5 py-0.2 rounded border border-blue-200/60">
                              Layanan
                            </span>
                          )}
                          <p className="text-xs text-slate-400 truncate">{product.category_name || (isService ? "Layanan" : "Tanpa Kategori")}</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex-shrink-0">
                      {isSelected ? (
                        <button
                          type="button"
                          onClick={() => onRemove && onRemove(product.product_id || product.id)}
                          className="px-3 py-1.5 bg-rose-500 hover:bg-rose-600 text-white rounded-lg text-xs font-semibold transition-colors shadow-xs flex items-center gap-1.5"
                          title="Hapus dari Best Seller">
                          <X className="w-4 h-4" />
                          Hapus
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => onSelect && onSelect(product.product_id || product.id)}
                          className="px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-xs font-semibold transition-colors shadow-xs flex items-center gap-1">
                          <Plus className="w-3.5 h-3.5" />
                          Tambah
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
